import React from 'react';
import { Control } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { InfoTooltip } from '../ui/InfoTooltip';
import { FormCard } from '../ui/FormCard';
import { MoneyInput } from '../ui/MoneyInput';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calculator } from 'lucide-react';

interface NursingHomeSectionProps {
  control: Control<any>;
  ownsPrimaryResidence: boolean;
  hasOtherProperties: boolean;
  hasFarmOrBusiness: boolean;
}

export function NursingHomeSection({ 
  control,
  ownsPrimaryResidence,
  hasOtherProperties,
  hasFarmOrBusiness
}: NursingHomeSectionProps) {
  // Check if the person has any property that could be used for a loan
  const hasPropertyForLoan = ownsPrimaryResidence || hasOtherProperties || hasFarmOrBusiness;
  
  return (
    <>
      <FormCard 
        title="Nursing Home Details" 
        description="Information about your nursing home care"
      >
        <div className="space-y-6">
          <FormField
            control={control}
            name="nursingHomeCost"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Weekly Nursing Home Cost (€)
                  <InfoTooltip content="The weekly cost of your chosen nursing home. This is optional but helps illustrate State support." />
                </FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <MoneyInput {...field} />
                  </FormControl>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-shrink-0 border-grandpal-green text-grandpal-green hover:bg-grandpal-green/10"
                    onClick={() => field.onChange(1250)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Use Average Cost
                  </Button>
                </div>
                <FormDescription>
                  Optional: Enter to see how much the State will contribute. The average weekly cost is around €1,250.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="yearsInCare"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Years Already in Care
                  <InfoTooltip content="If you're already in a nursing home, enter how many years. This affects the 3-year cap calculations." />
                </FormLabel>
                <FormControl>
                  <Input 
                    type="number"
                    min={0}
                    max={10}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  If you're not yet in care, leave as 0.
                </FormDescription>
              </FormItem>
            )}
          />

          {hasPropertyForLoan && (
            <FormField
              control={control}
              name="useLoan"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Use Nursing Home Loan (defer property contribution)
                      <InfoTooltip content="The Nursing Home Loan lets you defer paying the contribution from your property until later, typically after your death." />
                    </FormLabel>
                    <FormDescription>
                      This allows you to postpone paying the property portion of your contribution, with the State recovering it later.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          )}
        </div>
      </FormCard>
    </>
  );
}
